// =====================================================
// TURN90 CONSTANTS
// Centralized enums and constants matching DB schema
// =====================================================

// User roles as defined in app_users table
export const USER_ROLES = {
  OPS_MANAGER: 'Ops Manager',
  TEAM_LEAD: 'Team Lead',
  PROGRAM_STAFF: 'Program Staff',
  FINANCE: 'Finance',
  EXEC: 'Exec',
  TRAINEE: 'Trainee',
  ADMIN: 'Admin',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// Location types
export const LOCATION_TYPES = {
  CENTER: 'Center',
  WAREHOUSE: 'Warehouse',
  OFFICE: 'Office',
} as const;

export type LocationType = (typeof LOCATION_TYPES)[keyof typeof LOCATION_TYPES];

// Pre-defined locations (from seed data)
export const LOCATIONS = {
  CHARLESTON: { code: 'CHS', name: 'Charleston' },
  COLUMBIA: { code: 'CAE', name: 'Columbia' },
  SPARTANBURG: { code: 'SPA', name: 'Spartanburg' },
} as const;

// Order statuses
export const ORDER_STATUSES = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On Hold',
} as const;

export type OrderStatus = (typeof ORDER_STATUSES)[keyof typeof ORDER_STATUSES];

// Order priorities
export const ORDER_PRIORITIES = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
} as const;

export type OrderPriority = (typeof ORDER_PRIORITIES)[keyof typeof ORDER_PRIORITIES];

// Order sources
export const ORDER_SOURCES = {
  DIRECT: 'Direct',
  WEBSITE: 'Website',
  PHONE: 'Phone',
  EMAIL: 'Email',
  REFERRAL: 'Referral',
} as const;

export type OrderSource = (typeof ORDER_SOURCES)[keyof typeof ORDER_SOURCES];

// Project statuses
export const PROJECT_STATUSES = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Production',
  QC: 'QC',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On Hold',
} as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[keyof typeof PROJECT_STATUSES];

// Trainee program statuses
export const PROGRAM_STATUSES = {
  ACTIVE: 'Active',
  GRADUATED: 'Graduated',
  WITHDRAWN: 'Withdrawn',
  ON_LEAVE: 'On Leave',
  TERMINATED: 'Terminated',
} as const;

export type ProgramStatus = (typeof PROGRAM_STATUSES)[keyof typeof PROGRAM_STATUSES];

// Work assignment statuses
export const ASSIGNMENT_STATUSES = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No Show',
} as const;

export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[keyof typeof ASSIGNMENT_STATUSES];

// Shift quarters (Q1-Q4 throughout the day)
export const SHIFT_QUARTERS = {
  Q1: 'Q1',
  Q2: 'Q2',
  Q3: 'Q3',
  Q4: 'Q4',
} as const;

export type ShiftQuarter = (typeof SHIFT_QUARTERS)[keyof typeof SHIFT_QUARTERS];

// QC check results
export const QC_RESULTS = {
  PASS: 'Pass',
  FAIL: 'Fail',
  CONDITIONAL: 'Conditional',
} as const;

export type QCResult = (typeof QC_RESULTS)[keyof typeof QC_RESULTS];

// QuickBooks sync statuses
export const QB_SYNC_STATUSES = {
  SYNCED: 'Synced',
  PENDING: 'Pending',
  ERROR: 'Error',
  NOT_LINKED: 'Not Linked',
} as const;

export type QBSyncStatus = (typeof QB_SYNC_STATUSES)[keyof typeof QB_SYNC_STATUSES];

// Service types (from seed data)
export const SERVICE_TYPES = {
  KITTING: 'Kitting',
  QC: 'QC',
  PACKAGING: 'Packaging',
  LIGHT_MANUFACTURING: 'Light Manufacturing',
  SORTING: 'Sorting',
  LABELING: 'Labeling',
  DATA_ENTRY: 'Data Entry',
} as const;

export type ServiceTypeName = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES];

// Certification levels (1-4 skill progression)
export const CERTIFICATION_LEVELS = {
  LEVEL_1: 1, // Beginner
  LEVEL_2: 2, // Basic
  LEVEL_3: 3, // Intermediate
  LEVEL_4: 4, // Advanced
} as const;

export type CertificationLevel =
  (typeof CERTIFICATION_LEVELS)[keyof typeof CERTIFICATION_LEVELS];

// Material categories (from Phase 2 seed data)
export const MATERIAL_CATEGORIES = {
  PACKAGING: 'Packaging',
  LABELS: 'Labels',
  FASTENERS: 'Fasteners',
  COMPONENTS: 'Components',
  CONSUMABLES: 'Consumables',
  DOCUMENTS: 'Documents',
} as const;

export type MaterialCategory =
  (typeof MATERIAL_CATEGORIES)[keyof typeof MATERIAL_CATEGORIES];

// Default payment terms
export const PAYMENT_TERMS = {
  NET_15: 'Net 15',
  NET_30: 'Net 30',
  NET_45: 'Net 45',
  NET_60: 'Net 60',
  DUE_ON_RECEIPT: 'Due on Receipt',
} as const;

export type PaymentTerms = (typeof PAYMENT_TERMS)[keyof typeof PAYMENT_TERMS];

// Price agreement statuses
export const AGREEMENT_STATUSES = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  TERMINATED: 'Terminated',
  PENDING_APPROVAL: 'Pending Approval',
} as const;

export type AgreementStatus =
  (typeof AGREEMENT_STATUSES)[keyof typeof AGREEMENT_STATUSES];

// Quality event severities
export const QUALITY_SEVERITIES = {
  MINOR: 'Minor',
  MAJOR: 'Major',
  CRITICAL: 'Critical',
} as const;

export type QualitySeverity =
  (typeof QUALITY_SEVERITIES)[keyof typeof QUALITY_SEVERITIES];

// Issue log statuses
export const ISSUE_STATUSES = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  ESCALATED: 'Escalated',
} as const;

export type IssueStatus = (typeof ISSUE_STATUSES)[keyof typeof ISSUE_STATUSES];

// Activity log action types
export const ACTION_TYPES = {
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  EXPORT: 'Export',
  VIEW: 'View',
  APPROVE: 'Approve',
  REJECT: 'Reject',
} as const;

export type ActionType = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];
